import React from 'react';
import { array, string, bool, func } from 'prop-types';
import { compose } from 'redux';
import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import { Form as FinalForm } from 'react-final-form';
import classNames from 'classnames';
import {
  Form,
  FieldTextInput,
  IconSpinner,
  StagedAttachmentChips,
  AttachmentHints,
  useAttachmentFilePicker,
  useUnsavedAttachmentsWarning,
  keepComposerFocus,
} from '../../components';
import { propTypes } from '../../util/types';
import { MAX_MESSAGE_ATTACHMENTS } from '../../util/fileAttachments';
import { CiWarning } from 'react-icons/ci';
import { FiPaperclip, FiSend } from 'react-icons/fi';

import css from './SendMessageForm.css';

const warningIcon = <CiWarning />;

const SendMessageFormComponent = props => {
  const { stagedAttachments, onUploadAttachment, onRemoveAttachment, intl } = props;

  const {
    fileInputRef,
    pickErrorId,
    clearPickError,
    handleFilesSelected,
    openFilePicker,
  } = useAttachmentFilePicker({ stagedAttachments, onUploadAttachment });

  const hasStagedAttachments = stagedAttachments.length > 0;
  useUnsavedAttachmentsWarning(hasStagedAttachments);

  const handleRemoveAttachment = tempId => {
    clearPickError();
    onRemoveAttachment(tempId);
  };

  return (
    <FinalForm
      {...props}
      render={formRenderProps => {
        const {
          rootClassName,
          className,
          messagePlaceholder,
          handleSubmit,
          inProgress,
          sendMessageError,
          invalid,
          form,
          formId,
          values,
        } = formRenderProps;

        const classes = classNames(rootClassName || css.root, className);

        const anyUploading = stagedAttachments.some(a => a.state === 'uploading');
        const anyFailed = stagedAttachments.some(a => a.state === 'failed');
        const hasReadyAttachments = stagedAttachments.some(
          a => a.state === 'pendingVerification' || a.state === 'available'
        );

        const submitInProgress = inProgress;
        // Failed uploads must be removed before sending, and in-flight uploads
        // must finish so their files can be attached to the outgoing message.
        const submitDisabled =
          invalid ||
          submitInProgress ||
          anyUploading ||
          anyFailed ||
          (!values.message && !hasReadyAttachments);
        const attachDisabled = stagedAttachments.length >= MAX_MESSAGE_ATTACHMENTS;
        const attachFilesLabel = intl.formatMessage({
          id: 'MessageAttachmentsPicker.attachFilesAriaLabel',
        });
        const sendMessageLabel = intl.formatMessage({ id: 'SendMessageForm.sendMessageAriaLabel' });
        // Empty field → center everything on one line; once there's text (which
        // may wrap to several lines) → pin the buttons to the bottom.
        const hasMessageContent = !!values.message;

        // The attach button lives at the start of the chip row (compacted) once
        // files are staged, otherwise inline in the input row.
        const uploadButton = (
          <button
            type="button"
            className={classNames(css.upload, { [css.uploadCompact]: hasStagedAttachments })}
            onMouseDown={keepComposerFocus}
            onClick={openFilePicker}
            disabled={attachDisabled}
            title={attachFilesLabel}
            aria-label={attachFilesLabel}
          >
            <FiPaperclip className={css.uploadIcon} />
          </button>
        );

        return (
          <Form className={classes} onSubmit={values => handleSubmit(values, form)}>
            {sendMessageError ? (
              <p className={css.error}>
                <FormattedMessage id="SendMessageForm.sendFailed" />
              </p>
            ) : null}
            <div className={css.composer}>
              {hasStagedAttachments ? (
                <StagedAttachmentChips
                  className={css.attachmentChips}
                  attachments={stagedAttachments}
                  onRemove={handleRemoveAttachment}
                >
                  {uploadButton}
                </StagedAttachmentChips>
              ) : null}
              <AttachmentHints attachments={stagedAttachments} pickErrorId={pickErrorId} />
              <div
                className={classNames(css.inputRow, {
                  [css.inputRowBottom]: hasMessageContent,
                })}
              >
                {hasStagedAttachments ? null : uploadButton}
                <input
                  ref={fileInputRef}
                  className={css.fileInput}
                  type="file"
                  multiple
                  onChange={handleFilesSelected}
                />
                <FieldTextInput
                  className={css.field}
                  inputRootClass={css.textarea}
                  type="textarea"
                  id={formId ? `${formId}.message` : 'message'}
                  name="message"
                  placeholder={messagePlaceholder}
                  icon={warningIcon}
                  showSpacer={false}
                />
                {/* Deliberately not type="submit": in a sandboxed tab without
                    allow-forms the browser drops a native form submission before
                    any submit event fires, so onSubmit would never run. Submitting
                    from the click keeps sending independent of that. */}
                <button
                  type="button"
                  onClick={() => handleSubmit()}
                  className={classNames(css.submitButton, {
                    [css.submitButtonLoading]: submitInProgress,
                  })}
                  disabled={submitDisabled}
                  title={sendMessageLabel}
                  aria-label={sendMessageLabel}
                >
                  {submitInProgress ? (
                    <IconSpinner rootClassName={css.sendSpinner} />
                  ) : (
                    <FiSend className={css.sendIcon} />
                  )}
                </button>
              </div>
            </div>
          </Form>
        );
      }}
    />
  );
};

SendMessageFormComponent.defaultProps = {
  rootClassName: null,
  className: null,
  inProgress: false,
  messagePlaceholder: null,
  onFocus: () => null,
  onBlur: () => null,
  sendMessageError: null,
  stagedAttachments: [],
  onUploadAttachment: () => null,
  onRemoveAttachment: () => null,
};

SendMessageFormComponent.propTypes = {
  rootClassName: string,
  className: string,
  inProgress: bool,

  messagePlaceholder: string,
  onSubmit: func.isRequired,
  onFocus: func,
  onBlur: func,
  sendMessageError: propTypes.error,
  stagedAttachments: array,
  onUploadAttachment: func,
  onRemoveAttachment: func,

  // from injectIntl
  intl: intlShape.isRequired,
};

const SendMessageForm = compose(injectIntl)(SendMessageFormComponent);

SendMessageForm.displayName = 'SendMessageForm';

export default SendMessageForm;
